/**
 * Chatbar / AI Usage Panel
 *
 * Renders AI conversation + message metrics from
 * /api/admin/analytics/chatbar.
 */
import { Bot, MessageSquare, Users, Loader2 } from "lucide-react";
import {
  Bar,
  BarChart,
  CartesianGrid,
  Legend,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";

import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  useAdminChatbar,
  type AnalyticsRange,
} from "@/hooks/useAdminAnalytics";

interface Props {
  range: AnalyticsRange;
}

export function ChatbarPanel({ range }: Props) {
  const { data, isLoading, error } = useAdminChatbar(range);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-16">
        <Loader2 className="h-8 w-8 animate-spin text-[#B87333]" />
      </div>
    );
  }

  if (error || !data) {
    return (
      <Card>
        <CardContent className="py-12 text-center text-slate-600">
          Unable to load chatbar analytics.
        </CardContent>
      </Card>
    );
  }

  const stats = [
    {
      label: "Conversations Started",
      value: data.conversations,
      icon: <Bot className="h-5 w-5" />,
    },
    {
      label: "Messages Exchanged",
      value: data.messages,
      icon: <MessageSquare className="h-5 w-5" />,
    },
    {
      label: "Active Users",
      value: data.active_users,
      icon: <Users className="h-5 w-5" />,
    },
    {
      label: "Avg. Msgs / Conversation",
      value: data.avg_messages_per_conversation,
      icon: <MessageSquare className="h-5 w-5" />,
    },
  ];

  const dailyData = data.daily_conversations.map((d) => ({
    date: d.date.slice(5),
    conversations: d.conversations,
  }));

  const roleData = Object.entries(data.messages_by_role).map(
    ([role, count]) => ({ role, count }),
  );

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {stats.map((s) => (
          <Card key={s.label} className="border-2">
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-sm font-medium text-slate-600">
                {s.label}
              </CardTitle>
              <div className="p-2 bg-slate-100 rounded-lg text-slate-600">
                {s.icon}
              </div>
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold text-slate-900">
                {typeof s.value === "number" && Number.isInteger(s.value)
                  ? s.value.toLocaleString()
                  : s.value}
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Conversations Over Time</CardTitle>
          <CardDescription>
            New AI conversations started each day.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="h-72">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={dailyData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="date" />
                <YAxis allowDecimals={false} />
                <Tooltip />
                <Legend />
                <Line
                  type="monotone"
                  dataKey="conversations"
                  stroke="#B87333"
                  strokeWidth={2}
                />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Messages by Role</CardTitle>
          <CardDescription>
            Breakdown of message authorship in the window.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={roleData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="role" />
                <YAxis allowDecimals={false} />
                <Tooltip />
                <Bar dataKey="count" fill="#B87333" />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Conversations by Scope</CardTitle>
          <CardDescription>
            Where users started chats (course/lesson context vs. global).
          </CardDescription>
        </CardHeader>
        <CardContent>
          {data.conversations_by_scope.length === 0 ? (
            <p className="text-sm text-slate-500">
              No conversations in this range.
            </p>
          ) : (
            <div className="space-y-2">
              {data.conversations_by_scope.map((s) => (
                <div
                  key={s.scope}
                  className="flex items-center justify-between p-3 bg-slate-50 rounded-lg"
                >
                  <span className="font-medium text-slate-800">{s.scope}</span>
                  <span className="text-lg font-bold text-[#B87333]">
                    {s.count.toLocaleString()}
                  </span>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

export default ChatbarPanel;
